import imageCompression from 'browser-image-compression'

export const readAsDataURL = async (file: File): Promise<string | null> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      resolve(typeof e.target?.result == 'string' ? e.target?.result : null)
    }
    reader.onerror = (e) => {
      reject(e)
    }
    reader.readAsDataURL(file)
  })
}

// https://github.com/foodies-prime/food_api/blob/375f9020b211892fccc2b4921997f306056febb2/app/models/service/image_service.go#L287
const maxImageSize = {
  user_icon: 400,
  restaurant_cover: 1200,
  restaurant_thumbnail: 600,
  restaurant_photo_clip: 1200,
  dinner_thumbnail: 600,
  chat_space_icon: 600,
  chat_message: 1200,
  tweet: 1200,
  tweet_reply: 900,
}

export const preprocessImages = async (
  files: File[],
  type: keyof typeof maxImageSize
): Promise<File[] | undefined> => {
  const result: File[] = []
  for (const file of files) {
    const processed = await preprocessImage(file, type)
    if (processed == null) return undefined
    result.push(processed)
  }

  return result
}

export const preprocessImage = async (
  file: File,
  type: keyof typeof maxImageSize
): Promise<File | undefined> => {
  const raw = isRaw(file)
  const heic = await isHeic(file)
  const converted = heic
    ? // workaround to prevent error `ReferenceError: window is not defined`
      await convertHeic(file)
    : raw
    ? await convertRaw(file)
    : file

  if (converted == null) {
    return
  }

  const compressedFile = await imageCompression(
    new File([converted], file.name, {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    }),
    {
      maxWidthOrHeight: maxImageSize[type],
      useWebWorker: false,
      initialQuality: heic ? 0.7 : undefined,
    }
  )

  // compressedFile は File のインスタンスではないので、
  // あらためて File のインスタンスを作成する
  return new File([compressedFile], file.name, {
    type: 'image/jpeg',
    lastModified: file.lastModified,
  })
}

const isRaw = (file: File): boolean => {
  // windows: image/DNG
  // macOS/iOS: image/x-adobe-dng
  return file.type === 'image/DNG' || file.type === 'image/x-adobe-dng'
}

const convertRaw = async (file: File): Promise<Uint8Array> => {
  return window.dcraw(new Uint8Array(await file.arrayBuffer()), {
    extractThumbnail: true,
  })
}

// png 等と違って heic は仕様上固定の magic header がないので
// ImageMagick 等の先行事例を参考にして、なんとなく現実で問題なく動きそうな関数を用意
// https://github.com/strukturag/libheif/issues/83
// https://github.com/ImageMagick/ImageMagick/issues/1357#issuecomment-430968725
// https://github.com/file/file/blob/84eeb6f7ad24017f4248836defc08133fa86e8c7/magic/Magdir/animation#L191-L219
// $ heif_and_proraw/IMG_3168.HEIC | head
// 00000000  00 00 00 28 66 74 79 70  68 65 69 63 00 00 00 00  |...(ftypheic....|
// 00000010  6d 69 66 31 4d 69 48 45  4d 69 50 72 6d 69 61 66  |mif1MiHEMiPrmiaf|
const isHeic = async (file: File): Promise<boolean> => {
  // iOS や macOS では image/heic が入るが、windows では空
  if (file.type === 'image/heic') return true

  const bytes = new Uint8Array(await file.slice(4, 12).arrayBuffer())
  const magicHeaders = [
    'ftypmif1',
    'ftypmsf1',
    'ftypheic',
    'ftypheix',
    'ftyphevc',
    'ftyphevx',
  ]
  const matched = magicHeaders
    .map((header) => header.split('').map((ch) => ch.charCodeAt(0)))
    .find((pattern) => pattern.every((p, i) => bytes[i] === p))

  return matched != null
}

const convertHeic = async (file: File): Promise<Blob | undefined> => {
  // シミュレータ等では canvas error の例外がキャッチできるので、heif2any から返ってくる code=5 のエラー
  // を処理すれば良いが、実機ではブラウザ側で再読み込みが走ってしまうため、例外を捕捉できない。
  // 上限を動的に取れないので、固定値になるが iOS で w*h >= 16777216 な画像をアップロードしようとした場合に事前に検知する
  const libheif = window.libheif()
  const decoder = new libheif.HeifDecoder()
  const data = decoder.decode(await file.arrayBuffer())[0]
  if (navigator.userAgent.match(/(iPhone|iPod)/) != null) {
    if (data.get_width() * data.get_height() >= 16777216) {
      alert(
        'アップロードできる画像サイズの上限を超えています。画像を縮小してから再度お試しください。'
      )
      return
    }
  }
  // workaround to prevent error `ReferenceError: window is not defined`
  const heic2any = (await import('heic2any')).default
  const converted = heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.7,
  })
  return Array.isArray(converted) ? converted[0] : converted
}
